/**
 * Error Boundary Component for React
 * Catches JavaScript errors anywhere in child component tree
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: unknown[];
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('Error Boundary caught an error:', error, errorInfo);
    }

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // Here you could also log to error reporting service like Sentry
    // logErrorToService(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Reset error state when resetKeys change
    if (
      this.state.hasError &&
      this.props.resetKeys &&
      prevProps.resetKeys &&
      this.props.resetKeys.some((key, index) => key !== prevProps.resetKeys![index])
    ) {
      this.reset();
    }
  }

  reset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <DefaultErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.reset}
        />
      );
    }

    return this.props.children;
  }
}

// ==========================================
// Default Error Fallback UI
// ==========================================

interface DefaultErrorFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
}

function DefaultErrorFallback({ error, errorInfo, onReset }: DefaultErrorFallbackProps) {
  const isDev = import.meta.env.DEV;

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface p-4">
      <div className="w-full max-w-lg rounded-card border-2 border-line bg-white p-8 text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-pill bg-error-bg">
          <AlertTriangle className="h-8 w-8 text-error-text" />
        </div>

        <h1 className="mb-2 text-2xl font-extrabold text-ink">Terjadi Kesalahan</h1>

        <p className="mb-6 text-body">
          Maaf, terjadi kesalahan yang tidak terduga. Tim kami sudah diberitahu.
        </p>

        {isDev && error && (
          <details className="mb-6 rounded-lg bg-surface-2 p-4 text-left">
            <summary className="mb-2 cursor-pointer font-bold text-body">
              Detail error (hanya development)
            </summary>
            <div className="space-y-2">
              <div>
                <p className="text-sm font-bold text-error-text">
                  {error.name}: {error.message}
                </p>
              </div>
              {error.stack && (
                <pre className="max-h-40 overflow-auto rounded bg-line p-2 text-xs text-body">
                  {error.stack}
                </pre>
              )}
              {errorInfo?.componentStack && (
                <div>
                  <p className="mt-2 text-sm font-bold text-body">Component stack:</p>
                  <pre className="max-h-40 overflow-auto rounded bg-line p-2 text-xs text-body">
                    {errorInfo.componentStack}
                  </pre>
                </div>
              )}
            </div>
          </details>
        )}

        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <button
            onClick={onReset}
            className="flex items-center justify-center gap-2 rounded-pill bg-ink px-6 py-3 font-bold text-white transition-colors duration-150 ease-out hover:bg-ink/90"
          >
            <RefreshCw className="h-4 w-4" />
            Coba Lagi
          </button>

          <a
            href="/"
            className="flex items-center justify-center gap-2 rounded-pill border-2 border-ink px-6 py-3 font-bold text-ink transition-colors duration-150 ease-out hover:bg-surface-2"
          >
            <Home className="h-4 w-4" />
            Kembali ke Beranda
          </a>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// Route Error Boundary
// ==========================================

interface RouteErrorBoundaryProps {
  children: ReactNode;
}

export function RouteErrorBoundary({ children }: RouteErrorBoundaryProps) {
  return (
    <ErrorBoundary
      fallback={
        <div className="flex flex-col items-center justify-center px-4 py-16">
          <AlertTriangle className="mb-4 h-12 w-12 text-error-text" />
          <h2 className="mb-2 text-xl font-extrabold text-ink">Gagal Memuat Halaman</h2>
          <p className="mb-4 text-center text-body">Terjadi kesalahan saat memuat halaman ini.</p>
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 rounded-pill bg-ink px-4 py-2 font-bold text-white hover:bg-ink/90"
          >
            <RefreshCw className="h-4 w-4" />
            Muat Ulang
          </button>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
}

// ==========================================
// Query Error Boundary
// ==========================================

interface QueryErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

export function QueryErrorFallback({ error, resetErrorBoundary }: QueryErrorFallbackProps) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-8">
      <AlertTriangle className="mb-3 h-10 w-10 text-error-text" />
      <h3 className="mb-2 text-lg font-extrabold text-ink">Gagal Memuat Data</h3>
      <p className="mb-4 max-w-md text-center text-sm text-body">
        {error.message || 'Terjadi kesalahan saat mengambil data dari server.'}
      </p>
      <button
        onClick={resetErrorBoundary}
        className="flex items-center gap-2 rounded-pill bg-ink px-4 py-2 text-sm font-bold text-white hover:bg-ink/90"
      >
        <RefreshCw className="h-4 w-4" />
        Coba Lagi
      </button>
    </div>
  );
}

export default ErrorBoundary;
